// Project: GalaxyComplete
// Created: 9/25/20 by sammy
// File: GalaxyMigrateDeploymentsList

import { alpha, Box, Button, Chip, LinearProgress, Link, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { createColumnHelper, PaginationState } from "@tanstack/react-table";
import { sub } from "date-fns";
import { DeploymentInfo } from "gc-web-proto/galaxycompletepb/apipb/domainpb/deployment_pb";
import { GmDeploymentClustersType } from "gc-web-proto/galaxycompletepb/apipb/domainpb/enumpb/gm_deployment_clusters_type_pb";
import { GalaxyMigrateDeploymentInfo } from "gc-web-proto/galaxycompletepb/apipb/domainpb/galaxymigrate_pb";
import { observer } from "mobx-react-lite";
import * as React from "react";
import { useCallback, useState } from "react";
import { AiOutlineCluster } from "react-icons/ai";
import { GoDotFill } from "react-icons/go";
import { HiOutlineClock } from "react-icons/hi";
import { IoIosWarning } from "react-icons/io";
import { MdLabelOutline } from "react-icons/md";
import EmptyHostsTable from "../../assets/empty_states/empty-host-list.svg";
import EmptyRelayTable from "../../assets/empty_states/empty-management-relays.svg";
import { DeleteIcon, HostEnvironmentIcon, LicenseIcon, OnlineStatusIcon } from "../../common/CommonIcons";
import { DeploymentQueryKeys } from "../../common/QueryKeys";
import { ActionConfig, ActionMenuButton } from "../../common/actions/CommonActions";
import { OutlinedCard } from "../../common/card/OutlinedCard";
import { SelectableBox } from "../../common/card/SelectableCard";
import { QueryTable } from "../../common/table/QueryTable";
import { EmptyTableConfig } from "../../common/table/TableCommon";
import { BlackTooltip } from "../../common/tooltip/ColorTooltip";
import { formatDurationFromSeconds, formatKnownDataType, KnownDataType } from "../../common/utils/formatter";
import { useIsOperatorView } from "../auth/AuthenticatedViews";
import { useLinearProgressStyle } from "../cmcMigration/CmcMigrationCommon";
import { QueryResultWrapper } from "../core/data/QueryResultWrapper";
import { useGlobalDialogState } from "../core/dialog/GlobalDialogState";
import { getDeploymentConnectionStyle, HostOSIcon, parseCpuCountsFromCpuString } from "../deployment/DeploymentCommon";
import { NewDeploymentButton } from "../deployment/NewDeploymentInstructions";
import {
    useDeleteHostLicense,
    useGetGalaxyMigrateDeploymentClusterDetails,
    useListGalaxyMigrateDeployments,
    useRemoveGmDeployment,
} from "../deployment/deployment_hooks";
import { AppHintID, KnownArticle, LearnMoreButton } from "../help/HelpCommon";
import { useOpenHelpSearch } from "../help/hooks/help_hooks";
import { LabelChip } from "../labels/labelCommon";
import { useIsDesktop } from "../layout/MainLayout";
import { getHostEnvDisplayName, useNavigateToDeploymentDetails } from "./GalaxyMigrateCommon";
import { Trans, useTranslation } from "react-i18next";
// ======================
// GalaxyMigrateDeploymentsList
// ======================
interface GalaxyMigrateDeploymentsListProps {
    projectId: string;
    relayOnly?: boolean;
}

export const GalaxyMigrateDeploymentsList: React.FC<GalaxyMigrateDeploymentsListProps> = observer((p) => {
    const { t } = useTranslation(["galaxymigrate", "common"]);
    const { projectId, relayOnly } = p;
    const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
        pageIndex: 1,
        pageSize: 30,
    });

    const deploymentsList = useListGalaxyMigrateDeployments(projectId, false, pageIndex, pageSize, [], relayOnly, true, true);
    const openHelpSearch = useOpenHelpSearch();
    const theme = useTheme();
    const progressStyle = useLinearProgressStyle(true);
    const isOperator = useIsOperatorView();
    const deleteLicense = useDeleteHostLicense("list");
    const addConfirmDialog = useGlobalDialogState((s) => s.addConfirmDialog);
    const removeDeployment = useRemoveGmDeployment();
    const isMobileView = !useIsDesktop();

    const handleDeleteLicense = async (deployment: DeploymentInfo.AsObject) => {
        const confirmed = await addConfirmDialog({
            title: t("hostLicense.delete.title"),
            message: (
                <Box>
                    <Typography>
                        <Trans t={t} i18nKey="hostLicense.delete.irreversible" values={{ hostName: deployment.systemName }} components={{ host: <strong /> }} />
                    </Typography>
                    <br />
                    <Typography color={theme.palette.warning.main}>{t("hostLicense.delete.capacityWarning")}</Typography>
                    <br />
                </Box>
            ),
            autoConfirmationQuestionLine: true,
            typeToConfirm: "DELETE",
            okButtonProps: {
                color: "error",
            },
        });
        if (confirmed) {
            await deleteLicense.mutateAsync(deployment);
        }
    };

    const getEmptyTableProps = useCallback((): EmptyTableConfig => {
        if (relayOnly) {
            return {
                iconPath: EmptyRelayTable,
                title: t("deployments.emptyRelays.title"),
                message: t("deployments.emptyRelays.message"),
                actionButton: (
                    <Button color={"neutral"} variant={"outlined"} onClick={() => openHelpSearch(AppHintID.CMC_RELAY)}>
                        {t("common:links.learnMore")}
                    </Button>
                ),
                hideHelpLink: true,
            };
        } else {
            return {
                title: t("deployments.emptyHosts.title"),
                message: t("deployments.emptyHosts.message"),
                iconPath: EmptyHostsTable,
                actionButton: <NewDeploymentButton />,
            };
        }
    }, [relayOnly, openHelpSearch, t]);

    const getRowActions = (r: GalaxyMigrateDeploymentInfo.AsObject): ActionConfig[] => [
        {
            id: "delete-license",
            name: t("hostLicense.delete.title"),
            action: async () => await handleDeleteLicense(r.deployment),
            icon: <LicenseIcon />,
            hidden: !r.deployment.license,
        },
        {
            id: "remove",
            name: t("deployments.remove.title"),
            action: async () => {
                const confirmed = await addConfirmDialog({
                    title: t("deployments.remove.title"),
                    message: (
                        <Box>
                            <span>
                                <Trans
                                    t={t}
                                    i18nKey="deployments.remove.confirmation"
                                    values={{
                                        hostName: r.deployment.systemName,
                                        status: r.deployment.connected ? t("common:status.online") : t("common:status.offline"),
                                    }}
                                    components={{ host: <strong /> }}
                                />
                            </span>
                            <br />
                            {!!r.deployment.license && <Typography color={`warning.main`}>{t("deployments.remove.licenseWarning")}</Typography>}
                        </Box>
                    ),
                    autoConfirmationQuestionLine: false,
                    typeToConfirm: r.deployment.connected ? r.deployment.systemName : null,
                    okButtonProps: {
                        color: "error",
                    },
                });
                if (confirmed) {
                    await removeDeployment.mutateAsync(r.deployment.systemId);
                }
            },
            hidden: !isOperator,
            icon: <DeleteIcon />,
        },
    ];

    const columnHelper = createColumnHelper<GalaxyMigrateDeploymentInfo.AsObject>();

    const getHiddenColumns = () => {
        const hiddenColumns = [];
        if (!isOperator) {
            hiddenColumns.push("actions");
        }
        if (isMobileView) {
            hiddenColumns.push("resource");
        }
        return hiddenColumns;
    };

    const cols = [
        columnHelper.accessor((c) => c, {
            id: "host",
            header: t("deployments.columns.host"),
            cell: (props) => {
                const deploymentInfo = props.row.original;
                return <HostInfoDisplay deploymentInfo={deploymentInfo} />;
            },
        }),
        columnHelper.accessor((c) => c, {
            id: "resource",
            header: t("deployments.columns.resourceUsage"),
            meta: {
                style: {
                    textAlign: "center",
                },
            },
            cell: (props) => {
                const deploymentInfo = props.row.original;
                const hasCpuPercentage = !!deploymentInfo.deployment.systemUptime?.seconds;

                const memoryUsagePercentage = (deploymentInfo.memoryUsed / deploymentInfo.memory) * 100;
                return (
                    <Stack
                        minWidth={250}
                        alignItems={"flexStart"}
                        sx={{
                            opacity: deploymentInfo.deployment.connected ? 1 : 0.3,
                        }}
                    >
                        {hasCpuPercentage ? (
                            <BlackTooltip
                                title={
                                    <Box>
                                        <Typography variant={"caption"} fontWeight={600}>
                                            {deploymentInfo.cpu}
                                        </Typography>
                                        <br />
                                        <Typography variant={"caption"} color={"textSecondary"}>
                                            {t("deployments.resource.cpuModel")}
                                        </Typography>
                                    </Box>
                                }
                            >
                                <Stack
                                    //sx={getDeploymentConnectionStyle(deploymentInfo.deployment.connected)}
                                    direction={"row"}
                                    spacing={1}
                                    alignItems={"flex-end"}
                                >
                                    <Typography variant={"caption"}>{"CPU "}&nbsp;</Typography>
                                    <Stack>
                                        <Stack direction={"row"} spacing={1} justifyContent={"space-between"}>
                                            <Typography variant={"caption"}>
                                                {t("deployments.resource.cores", {
                                                    count: parseCpuCountsFromCpuString(deploymentInfo.cpu).cores,
                                                })}
                                            </Typography>
                                            <Typography variant={"caption"} color={`${getResourceUsageColor(deploymentInfo.cpuUsagePercentage)}.main`}>
                                                {formatKnownDataType(deploymentInfo.cpuUsagePercentage, KnownDataType.PERCENT)}
                                            </Typography>
                                        </Stack>
                                        <LinearProgress
                                            sx={{
                                                ...progressStyle,
                                                minWidth: 200,
                                            }}
                                            variant={"determinate"}
                                            value={deploymentInfo.cpuUsagePercentage}
                                            color={getResourceUsageColor(deploymentInfo.cpuUsagePercentage)}
                                        />
                                    </Stack>
                                </Stack>
                            </BlackTooltip>
                        ) : null}
                        {deploymentInfo.memoryUsed >= 0 && (
                            <Box pt={1}>
                                <Stack alignItems={"flex-start"} direction={"row"} spacing={1}>
                                    <Typography variant={"caption"}>{t("deployments.resource.memoryAbbreviation")}</Typography>
                                    <Stack>
                                        <LinearProgress
                                            sx={{
                                                ...progressStyle,
                                                minWidth: 200,
                                            }}
                                            variant={"determinate"}
                                            value={memoryUsagePercentage}
                                            color={getResourceUsageColor(memoryUsagePercentage)}
                                        />
                                        <Stack direction={"row"} justifyContent={"space-between"}>
                                            <Typography variant={"caption"}>
                                                {`${formatKnownDataType(deploymentInfo.memoryUsed, KnownDataType.CAPACITY)}/${formatKnownDataType(
                                                    deploymentInfo.memory,
                                                    KnownDataType.CAPACITY
                                                )}`}
                                            </Typography>
                                            <Typography variant={"caption"} color={`${getResourceUsageColor(memoryUsagePercentage)}.main`}>
                                                {formatKnownDataType(memoryUsagePercentage, KnownDataType.PERCENT)}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </Stack>
                            </Box>
                        )}
                    </Stack>
                );
            },
        }),

        columnHelper.accessor((c) => c, {
            id: "cluster",
            header: t("deployments.columns.cluster"),
            meta: {
                style: {
                    textAlign: "center",
                },
            },
            cell: (props) => {
                const deploymentInfo = props.row.original;
                const clusterInfo = deploymentInfo.clusterInfo;
                const clusterIconColor =
                    clusterInfo?.numberOfNodes > clusterInfo?.numberOfOnlineNodes
                        ? theme.palette.error.main
                        : clusterInfo?.numberOfRegisteredNodes < clusterInfo?.numberOfNodes
                        ? theme.palette.warning.main
                        : theme.palette.success.main;
                return clusterInfo ? (
                    <Box minWidth={150}>
                        <Tooltip
                            slotProps={{
                                tooltip: {
                                    sx: {
                                        backgroundColor: theme.palette.cirrus.light,
                                        border: `2px solid ${theme.palette.primary.main}`,
                                        borderRadius: 3,
                                        padding: 0,
                                    },
                                },
                            }}
                            title={
                                <Box width={250}>
                                    <ClusterInfo clusterId={clusterInfo.id} projectId={projectId} deploymentConnected={deploymentInfo.deployment.connected} />
                                </Box>
                            }
                        >
                            <Box>
                                <SelectableBox
                                    selected={false}
                                    cardProps={{
                                        sx: {},
                                    }}
                                >
                                    <Stack
                                        p={1}
                                        direction={"row"}
                                        alignItems={"center"}
                                        justifyContent={"center"}
                                        spacing={1}
                                        sx={{ opacity: deploymentInfo.deployment.connected ? 1 : 0.3 }}
                                    >
                                        <AiOutlineCluster size={24} color={clusterIconColor} />
                                        <Stack direction={"column"}>
                                            <Typography
                                                variant={"caption"}
                                                color={clusterInfo.numberOfRegisteredNodes < clusterInfo.numberOfNodes ? "warning.main" : "inherit"}
                                            >
                                                {t("deployments.cluster.deployedCount", {
                                                    deployed: clusterInfo.numberOfRegisteredNodes,
                                                    total: clusterInfo.numberOfNodes,
                                                })}
                                            </Typography>
                                            <Typography
                                                variant={"caption"}
                                                color={clusterInfo.numberOfOnlineNodes < clusterInfo.numberOfNodes ? "error.main" : "inherit"}
                                            >
                                                {t("deployments.cluster.onlineCount", {
                                                    online: clusterInfo.numberOfOnlineNodes,
                                                    total: clusterInfo.numberOfNodes,
                                                })}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </SelectableBox>
                            </Box>
                        </Tooltip>
                    </Box>
                ) : (
                    <Box width={"100%"} display={"flex"} justifyContent={"center"}>
                        {"--"}
                    </Box>
                );
            },
        }),
        columnHelper.accessor((c) => c, {
            id: "actions",
            header: t("common:actions.actions"),
            cell: (props) => {
                const deploymentInfo = props.row.original;
                return <ActionMenuButton actions={getRowActions(deploymentInfo)} />;
            },
        }),
    ];

    return (
        <Box pt={2}>
            <QueryTable
                data={deploymentsList.data?.itemsList}
                columns={cols}
                emptyTableConfig={getEmptyTableProps()}
                error={deploymentsList.error}
                isLoading={deploymentsList.isLoading}
                pageCount={deploymentsList.data?.pagerMeta?.totalPages || 0}
                pagination={{ pageIndex, pageSize }}
                setPagination={setPagination}
                refetch={deploymentsList.refetch}
                queryKey={DeploymentQueryKeys.listGmDeployments}
                hiddenColumns={getHiddenColumns()}
                totalItems={deploymentsList.data?.pagerMeta?.totalItems}
                itemTypeLabel={t("deployments.itemType")}
            />
        </Box>
    );
});

// ======================
// HostInfoDisplay
// ======================

interface HostInfoDisplayProps {
    deploymentInfo: GalaxyMigrateDeploymentInfo.AsObject;
}

const HIGH_LATENCY_THRESHOLD_MS = 1000;

const getLatencyInMs = (latency: { seconds?: number; nanos?: number } | undefined): number => {
    if (!latency) return 0;
    const seconds = latency.seconds ?? 0;
    const nanos = latency.nanos ?? 0;
    return seconds * 1000 + nanos / 1000000;
};

export const HostInfoDisplay: React.FC<HostInfoDisplayProps> = (p) => {
    const { t } = useTranslation("galaxymigrate");
    const { deploymentInfo } = p;
    const goToDeploymentDetails = useNavigateToDeploymentDetails();
    const theme = useTheme();
    const isMtdiNotRunning = !deploymentInfo.mtdiVersion || deploymentInfo.mtdiVersion === "0.0.0";
    const latencyMs = getLatencyInMs(deploymentInfo.deployment.connectionLatency);
    const hasHighLatency = deploymentInfo.deployment.connected && latencyMs >= HIGH_LATENCY_THRESHOLD_MS;
    return (
        <Box>
            {isMtdiNotRunning && deploymentInfo.deployment.connected ? (
                <Box mb={1}>
                    <Chip icon={<IoIosWarning size={14} />} size={"small"} color={"error"} label={t("deployments.status.mtdiNotRunning")} />
                </Box>
            ) : null}

            <Stack direction={"row"} alignItems={"center"} spacing={2}>
                <Box>
                    <Grid container alignItems={"center"} spacing={1}>
                        <Grid>
                            <Stack direction={"row"} alignItems={"center"} spacing={1}>
                                <OnlineStatusIcon online={deploymentInfo.deployment.connected} size={20} />

                                <Link variant={"body1"} fontWeight={600} onClick={() => goToDeploymentDetails(deploymentInfo.deployment.systemId)}>
                                    {deploymentInfo.deployment.systemName}
                                </Link>
                            </Stack>
                        </Grid>
                        <Grid>
                            <Typography color={"textSecondary"} variant={"subtitle2"}>
                                {"v"}
                                {deploymentInfo.deployment.version}
                            </Typography>
                        </Grid>
                        <Grid>
                            {deploymentInfo.deployment.labelsList.length > 0 && (
                                <Box pt={0.5}>
                                    <BlackTooltip
                                        title={
                                            <Box p={1}>
                                                <Stack spacing={1}>
                                                    {deploymentInfo.deployment.labelsList.map((l) => (
                                                        <LabelChip key={l.name} label={l.name} description={l.description} color={l.color} />
                                                    ))}
                                                </Stack>
                                            </Box>
                                        }
                                        arrow
                                    >
                                        <Box>
                                            <MdLabelOutline size={20} color={theme.palette.warning.main} />
                                        </Box>
                                    </BlackTooltip>
                                </Box>
                            )}
                        </Grid>
                    </Grid>
                    <GalaxyMigrationDeploymentOSDisplay deploymentInfo={deploymentInfo} />
                    <Box sx={getDeploymentConnectionStyle(deploymentInfo.deployment.connected)} pt={0.5}>
                        <Grid container alignItems={"center"} spacing={1}>
                            {!!deploymentInfo.deployment.cdcRelayServerAddress && (
                                <Grid>
                                    <BlackTooltip
                                        arrow
                                        title={
                                            <Box>
                                                <Typography variant={"subtitle2"} fontWeight={600}>
                                                    {t("relay.management.name")}
                                                </Typography>
                                                <Typography variant={"caption"} color={"textSecondary"}>
                                                    {deploymentInfo.deployment.cdcRelayServerAddress.split("/")[0]}
                                                </Typography>
                                            </Box>
                                        }
                                    >
                                        <Chip label={t("relay.management.shortName")} color={"success"} variant={"outlined"} size={"small"} />
                                    </BlackTooltip>
                                </Grid>
                            )}
                            {deploymentInfo.deployment.h2hRelayEnabled && (
                                <Grid>
                                    <Chip label={t("relay.h2h.name")} color={"success"} variant={"outlined"} size={"small"} />
                                </Grid>
                            )}
                            <Grid>
                                <GalaxyMigrationDeploymentEnvironmentDisplay deploymentInfo={deploymentInfo} />
                            </Grid>
                            <Grid>
                                <Stack direction={"row"} spacing={0.5} alignItems={"center"}>
                                    <HiOutlineClock size={16} />
                                    <BlackTooltip
                                        title={
                                            <Box>
                                                <Typography fontWeight={600} variant={"subtitle2"}>
                                                    {t("deployments.fields.lastCheckIn")}
                                                </Typography>
                                                <Typography variant={"caption"}>
                                                    {formatKnownDataType(deploymentInfo.deployment.lastCheckin, KnownDataType.DATE)}
                                                </Typography>
                                            </Box>
                                        }
                                        arrow
                                    >
                                        <Typography
                                            variant={"subtitle2"}
                                            sx={{
                                                textDecoration: "underline",
                                                textDecorationStyle: "dotted",
                                            }}
                                        >
                                            {formatKnownDataType(deploymentInfo.deployment.lastCheckin, KnownDataType.DATE_RELATIVE)}
                                        </Typography>
                                    </BlackTooltip>
                                </Stack>
                            </Grid>
                            {!!deploymentInfo.deployment.systemUptime?.seconds && (
                                <Grid>
                                    <BlackTooltip
                                        title={
                                            <Box>
                                                <Typography fontWeight={600} variant={"subtitle2"}>
                                                    {t("deployments.uptime.title")}
                                                </Typography>
                                                <Typography variant={"caption"}>
                                                    {t("deployments.uptime.lastReboot", {
                                                        date: formatKnownDataType(
                                                            sub(new Date(), { seconds: deploymentInfo.deployment.systemUptime?.seconds }),
                                                            KnownDataType.DATE
                                                        ),
                                                    })}
                                                </Typography>
                                            </Box>
                                        }
                                        arrow
                                    >
                                        <Typography
                                            variant={"subtitle2"}
                                            sx={{
                                                textDecoration: "underline",
                                                textDecorationStyle: "dotted",
                                            }}
                                        >
                                            {t("deployments.uptime.duration", {
                                                duration: formatDurationFromSeconds(deploymentInfo.deployment.systemUptime?.seconds),
                                            })}
                                        </Typography>
                                    </BlackTooltip>
                                </Grid>
                            )}

                            <Grid>
                                <BlackTooltip
                                    title={
                                        <Box>
                                            <Typography variant={"subtitle2"} fontWeight={"600"}>
                                                {t("deployments.fields.latency")}
                                            </Typography>
                                            {hasHighLatency && (
                                                <Typography variant={"caption"} color={"warning.main"}>
                                                    {t("deployments.status.highLatency")}
                                                </Typography>
                                            )}
                                        </Box>
                                    }
                                    arrow
                                >
                                    <Stack direction={"row"} spacing={0.5} alignItems={"center"}>
                                        {hasHighLatency && <IoIosWarning size={14} color={theme.palette.warning.main} />}
                                        <Typography
                                            variant={"subtitle2"}
                                            sx={{
                                                textDecoration: "underline",
                                                textDecorationStyle: "dotted",
                                                color: hasHighLatency ? "warning.main" : "inherit",
                                            }}
                                        >
                                            {!!deploymentInfo.deployment.connectionLatency
                                                ? formatKnownDataType(deploymentInfo.deployment.connectionLatency, KnownDataType.DURATION_MILLISECONDS)
                                                : null}
                                        </Typography>
                                    </Stack>
                                </BlackTooltip>
                            </Grid>

                            {deploymentInfo.deployment.location && (
                                <Grid>
                                    <BlackTooltip
                                        title={
                                            <Box>
                                                <Typography variant={"subtitle2"} fontWeight={600}>
                                                    {!!deploymentInfo.deployment.location.city
                                                        ? `${deploymentInfo.deployment.location.city}, ${deploymentInfo.deployment.location.region}`
                                                        : null}
                                                </Typography>
                                                <Typography variant={"caption"} color={"textSecondary"}>
                                                    {deploymentInfo.deployment.location.countryName}
                                                </Typography>
                                            </Box>
                                        }
                                        arrow
                                    >
                                        <Box pt={0.5}>{deploymentInfo.deployment.location?.countryFlag}</Box>
                                    </BlackTooltip>
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                </Box>
            </Stack>
        </Box>
    );
};

// ======================
// GalaxyMigrationDeploymentOSDisplay
// ======================
interface GalaxyMigrationDeploymentOSDisplayProps {
    deploymentInfo: GalaxyMigrateDeploymentInfo.AsObject;
    hideKernel?: boolean;
}

export const GalaxyMigrationDeploymentOSDisplay: React.FC<GalaxyMigrationDeploymentOSDisplayProps> = observer((p) => {
    const r = p.deploymentInfo;
    const os = `${r.osClass}${!p.hideKernel ? ` - ${r.kernel}` : ""}`;
    return (
        <Box display={"flex"} alignItems={"center"} sx={getDeploymentConnectionStyle(r.deployment.connected)}>
            <Stack direction={"row"} alignItems={"center"} pl={0}>
                <Typography variant={"body2"}>
                    <HostOSIcon size={12} os={os} /> &nbsp;&nbsp;
                    {os}
                </Typography>
            </Stack>
        </Box>
    );
});

// ======================
// GalaxyMigrationDeploymentEnvironmentDisplay
// ======================

interface GalaxyMigrationDeploymentEnvironmentDisplayProps {
    deploymentInfo: GalaxyMigrateDeploymentInfo.AsObject;
}

export const GalaxyMigrationDeploymentEnvironmentDisplay: React.FC<GalaxyMigrationDeploymentEnvironmentDisplayProps> = observer((p) => {
    const r = p.deploymentInfo;
    const env = r.deployment.hostEnvironment.value;

    return (
        <Stack direction={"row"} alignItems={"center"} sx={getDeploymentConnectionStyle(r.deployment.connected)}>
            <HostEnvironmentIcon env={env} size={16} /> &nbsp;&nbsp;
            <Typography variant={"body2"}>{getHostEnvDisplayName(env)}</Typography>
        </Stack>
    );
});

export const getResourceUsageColor = (usagePercent: number) => {
    if (usagePercent < 75) {
        return "success";
    }
    if (usagePercent < 90) {
        return "warning";
    }
    return "error";
};

// ======================
// ClusterInfo
// ======================

interface ClusterInfoProps {
    clusterId: number;
    projectId: string;
    deploymentConnected: boolean;
}

export const ClusterInfo: React.FC<ClusterInfoProps> = (p) => {
    const { t } = useTranslation("galaxymigrate");
    const { clusterId, projectId, deploymentConnected } = p;
    const cluster = useGetGalaxyMigrateDeploymentClusterDetails(projectId, clusterId);
    const clusterInfo = cluster.data?.clusterDetails.clusterInfo;
    const goToHostDetails = useNavigateToDeploymentDetails();
    const theme = useTheme();

    const getUnregisteredHostsList = () => {
        const array = [];
        for (let i = 0; i < clusterInfo?.numberOfNodes - clusterInfo?.numberOfRegisteredNodes; i++) {
            array.push(t("deployments.cluster.unregisteredHost"));
        }
        return array;
    };

    return (
        <Box
            sx={{
                opacity: deploymentConnected ? 1 : 0.3,
            }}
        >
            <QueryResultWrapper queryResult={cluster}>
                <Box p={2}>
                    <Typography fontWeight={600}>{clusterInfo?.clusterName}</Typography>
                    <Typography variant={"caption"} color={"textSecondary"}>
                        {clusterInfo?.clusterInfo.windowsFailoverClusterInfo?.domain}
                    </Typography>
                    {clusterInfo?.clusterType === GmDeploymentClustersType.GmDeploymentClustersType.WINDOWS_FAILOVER_CLUSTER && (
                        <Box pt={1}>
                            <Chip
                                label={t("deployments.cluster.windowsFailover")}
                                color={"primary"}
                                variant={"filled"}
                                size={"small"}
                                sx={{
                                    borderRadius: 1,
                                }}
                            />
                        </Box>
                    )}
                    <Box pt={2}>
                        <Typography variant={"subtitle2"}>
                            {t("deployments.cluster.registeredCount", {
                                registered: clusterInfo?.numberOfRegisteredNodes,
                                total: clusterInfo?.numberOfNodes,
                            })}
                        </Typography>
                    </Box>
                    <Box pt={1} width={"100%"}>
                        <OutlinedCard sx={{ maxWidth: "100%", background: "none" }}>
                            <Box p={1}>
                                {cluster.data?.clusterDetails.registeredHostsList.map((host) => {
                                    return (
                                        <Stack direction={"row"} spacing={1} alignItems={"center"}>
                                            <OnlineStatusIcon online={host.online} />
                                            <Link variant={"subtitle2"} onClick={() => goToHostDetails(host.deployment.systemId)}>
                                                {host.deployment.systemName}
                                            </Link>
                                        </Stack>
                                    );
                                })}
                                {getUnregisteredHostsList().map((h, i) => {
                                    return (
                                        <Stack
                                            key={i}
                                            direction={"row"}
                                            spacing={1}
                                            alignItems={"center"}
                                            sx={{
                                                color: alpha(`#fff`, 0.3),
                                            }}
                                        >
                                            <GoDotFill size={12} />
                                            <Typography variant={"subtitle2"}>{h}</Typography>
                                        </Stack>
                                    );
                                })}
                            </Box>
                        </OutlinedCard>
                    </Box>
                </Box>
                <Box
                    sx={{
                        background: theme.palette.cirrus.main,
                        borderBottomLeftRadius: 10,
                        borderBottomRightRadius: 10,
                        borderTop: `2px solid ${theme.palette.border.main}`,
                    }}
                >
                    <Box p={2}>
                        <Typography fontWeight={600}>{t("deployments.cluster.helpTitle")}</Typography>
                        <Typography variant={"caption"}>{t("deployments.cluster.helpDescription")}</Typography>
                        <Box pt={2}>
                            <LearnMoreButton articleID={KnownArticle.MIGRATING_CLUSTERS} variant={"outlined"} />
                        </Box>
                    </Box>
                </Box>
            </QueryResultWrapper>
        </Box>
    );
};
